import React, { useEffect } from 'react';
import { getMarkDown } from '../utils';
import { ReactComponent as CloseIcon } from '../../../assets/toast-icon-close-gray.svg';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon-google.svg';
import '../../../stylesheets/triagebot/Toast.scss';

//****************************************//

const ToastDialog = ({ message, close, onClose, fullscreen }) => {
    close = (close instanceof Function)
        ? close
        : () => { }

    onClose = (onClose instanceof Function)
        ? onClose
        : () => { }

    const type = message?.type.toLowerCase() || 'unknown';

    const header = (typeof message?.header === 'string')
        ? message.header
        : ''

    const subheader = (typeof message?.subheader === 'string')
        ? message.subheader
        : ''

    const body = message?.body || '';
    const closeText = (typeof message?.closeText === 'string' && message.closeText.trim().length > 0)
        ? message.closeText
        : 'Close';
    const hasDescription = subheader.trim().length > 0 || !!body;
    const descriptionId = [subheader.trim().length > 0 ? 'toast-subheader' : null, body ? 'toast-body' : null]
        .filter(Boolean)
        .join(' ') || undefined;

    const keyPressed = (e) => {
        if (e.keyCode === 13 || e.keyCode === 32)
            return;

        if (e.keyCode === 27)
            handleClose();
    }

    const handleClose = () => {
        // Store reference to trigger element before closing
        const triggerElement = message?.triggerElement;
        
        document.getElementById('toast').style.top = '100%';
        document.getElementById('toast-closer').style.opacity = '0';
        document.getElementById('toast-closer').style.display = 'none';
        onClose();

        setTimeout(() => {
            close();
            // Return focus to the triggering element after close completes
            if (triggerElement && typeof triggerElement.focus === 'function') {
                // Ensure element is focusable and restore focus
                setTimeout(() => {
                    try {
                        // Use preventScroll and let CSS :focus-visible handle visibility
                        triggerElement.focus({ preventScroll: true });
                    } catch (error) {
                        console.warn('Failed to restore focus to trigger element:', error);
                    }
                }, 10);
            }
        }, 500);
    }

    const updateSize = () => {
        const toastElement = document.getElementById('toast');
        const scrollContainer = document.getElementById('toast-scroll-container');
        const toastHeaderHeight = document.getElementById('toast-header').offsetHeight;
        const toastSubHeaderHeight = document.getElementById('toast-subheader').offsetHeight;
        const toastBodyHeight = document.getElementById('toast-body').offsetHeight;
        const toastTotalHeight = toastHeaderHeight + toastBodyHeight + toastSubHeaderHeight;
        
        const maxToastHeight = window.innerHeight;
        const paddingBottom =  window.getComputedStyle(document.getElementById('toast-body')).paddingBottom;
        const padding = Number(paddingBottom.slice(0,2))
        const toastHeight = (fullscreen || maxToastHeight < toastTotalHeight)
            ? maxToastHeight
            : toastTotalHeight;

        const toastTop = (type === 'nps')
            ? Math.max((maxToastHeight - toastHeight) / 2, 16)
            : (maxToastHeight - toastHeight);

        toastElement.style.top = toastTop + 'px';
        toastElement.style.bottom = (type === 'nps') ? 'auto' : '0';
        scrollContainer.style.height = (toastHeight-toastHeaderHeight-toastSubHeaderHeight-padding) + 'px';
        
       // if (type !== 'nps')
            document.getElementById('toast-closer').style.opacity = .15;
    }

    function handleTabbing(event) {
        if (event.key.toLowerCase() !== "tab") return;

        const target = event.target;
        const closeButton = document.getElementById('toast-close-button');
        const bottomCloseButton = document.querySelector('.close-popup-button');
        const firstButton = document.getElementById('toast-area').querySelector('button:not(#toast-close-button)');
        const headingEl = document.getElementById('toast-header-text');
        const toastBodyEl = document.getElementById('toast-body');

        // Tab from the toast container itself > move to heading first
        if (target === document.getElementById('toast') && !event.shiftKey) {
            event.preventDefault();
            headingEl.focus();
            return;
        }

        // Shift+Tab from heading > wrap to close button (last focusable)
        if (target === headingEl && event.shiftKey) {
            event.preventDefault();
            if (bottomCloseButton) {
                bottomCloseButton.focus();
            } else {
                closeButton.focus();
            }
            return;
        }

        // Shift+Tab from toast-body > heading
        if (target === toastBodyEl && event.shiftKey) {
            event.preventDefault();
            headingEl.focus();
            return;
        }

        if (!firstButton) {
            // No action buttons in toast-area. Cycle: heading > toast-body > close > heading
            if (!event.shiftKey && target === closeButton) {
                event.preventDefault();
                headingEl.focus();
            }
            return;
        }

        if (event.shiftKey) {
            // Back tabbing
            if (target === firstButton) {
                // At the first tabbable button
                event.preventDefault();
                if (bottomCloseButton) {
                    bottomCloseButton.focus();
                } else {
                    closeButton.focus();
                }
            } else if (target === bottomCloseButton) {
                event.preventDefault();
                closeButton.focus();
            }
            return;
        }

        if (target === closeButton) {
            event.preventDefault();
            firstButton.focus();
        } else if (target === firstButton && bottomCloseButton) {
            const lastButton = Array.from(document.getElementById('toast-area').querySelectorAll('button:not(#toast-close-button)')).pop();
            if (target === lastButton) {
                event.preventDefault();
                bottomCloseButton.focus();
            }
        } else if (target === bottomCloseButton) {
            event.preventDefault();
            closeButton.focus();
        }
    }

    useEffect(() => {
        window.addEventListener('resize', updateSize);
        updateSize();
        document.getElementById('toast-closer').style.display = 'block';
        return () => window.removeEventListener('resize', updateSize);
    })

    useEffect(() => {
        // Prevent screen readers from browsing outside the modal.
        // Walk up from the dialog to <body>, hide sibling elements at each level,
        // then restore their previous aria-hidden values when the dialog closes.
        const hiddenElements = [];
        let el = document.getElementById('toast-dialog');

        while (el && el !== document.body) {
            const parent = el.parentElement;

            if (parent) {
                Array.from(parent.children).forEach((sibling) => {
                    const tag = sibling.tagName.toLowerCase();

                    if (
                        sibling !== el &&
                        tag !== 'script' &&
                        tag !== 'style'
                    ) {
                        hiddenElements.push({
                            element: sibling,
                            ariaHidden: sibling.getAttribute('aria-hidden')
                        });
                        sibling.setAttribute('aria-hidden', 'true');
                    }
                });
            }

            el = parent;
        }

        return () => {
            hiddenElements.forEach(({ element, ariaHidden }) => {
                if (ariaHidden === null) {
                    element.removeAttribute('aria-hidden');
                    return;
                }

                element.setAttribute('aria-hidden', ariaHidden);
            });
        };
    }, [])

    useEffect(() => {
        const headingEl = document.getElementById('toast-header-text');
        if (headingEl) {
            headingEl.focus({ preventScroll: true });
            return;
        }

        if (message.focus instanceof Function) {
            message.focus();
            return;
        }

        document.getElementById('toast').focus();
    }, [message])

    return (
        <div
            id='toast-dialog'
            role='dialog'
            aria-modal='true'
            aria-label='Information'
            aria-describedby={hasDescription ? descriptionId : undefined}
            tabIndex='-1'
            onKeyDown={handleTabbing}
        >
            <div
                id='toast-closer'
                className='toast-closer'
                aria-hidden='true'
                onClick={handleClose}
            />
            <div id='toast'
                className={type}
                tabIndex='0'
                onKeyDown={handleTabbing}
            >
                <div id='toast-area' className={type}>
                    <div id='toast-header'>
                        {
                            (type === 'info') &&
                            <span className='info-icon'>
                                <InfoIcon style={{width : '21px', height: '21px', fill: 'var(--primary-color)'}} />
                            </span>
                        }
                        <h2 id='toast-header-text' className='headerText' style={{ "margin" : "0px" }} tabIndex='-1'> {getMarkDown(header)} </h2>
                    </div>
                    <div id='toast-subheader'>
                        {getMarkDown(subheader)}
                    </div>    
                    <div id='toast-scroll-container' className={(type === 'nps') ? 'nps-popup-frame' : ''} tabIndex='0'>
                        {
                            (type === 'nps') &&
                            <button
                                id='toast-close-button'
                                aria-label='Close'
                                onClick={handleClose}
                                onKeyDown={keyPressed}
                            >
                                <span className='nps-close-text'>{closeText}</span>
                                <CloseIcon />
                            </button>
                        }
                        <div id='toast-body' className={type}>
                            {
                                (typeof body === 'string')
                                    ? getMarkDown(body)
                                    : body
                            }
                        </div>
                    </div>
                </div>
                {
                    (type !== 'nps') &&
                    <button
                        id='toast-close-button'
                        aria-label='Close'
                        onClick={handleClose}
                        onKeyDown={keyPressed}
                    >
                        {(type === 'symptoms') && <span className='close-text'>{closeText}</span>}
                        <CloseIcon />
                    </button>
                }
            </div>
        </div>
    );
}

export default ToastDialog;